import React from "react";
import { FaUserCircle } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";

/**
 * Full Tailwind class strings per coach color index. Kept as complete strings
 * (never interpolated) so Tailwind's compiler retains them.
 * @type {Record<number, string>}
 */
const COACH_RING_CLASSES = {
  0: "ring-coach-0 bg-coach-0-bg text-coach-0",
  1: "ring-coach-1 bg-coach-1-bg text-coach-1",
  2: "ring-coach-2 bg-coach-2-bg text-coach-2",
  3: "ring-coach-3 bg-coach-3-bg text-coach-3",
  4: "ring-coach-4 bg-coach-4-bg text-coach-4",
  5: "ring-coach-5 bg-coach-5-bg text-coach-5",
  6: "ring-coach-6 bg-coach-6-bg text-coach-6",
  7: "ring-coach-7 bg-coach-7-bg text-coach-7",
};

const SIZE_CLASSES = {
  // `xs` exists for the dense support workspace, where a roster row and a
  // ticket's owner column are both ~20px.
  xs: "w-5 h-5 text-[9px]",
  sm: "w-7 h-7 text-xs",
  md: "w-9 h-9 text-sm",
  lg: "w-12 h-12 text-base",
};

/**
 * Build initials from a name: first letters of the first two words, uppercased.
 * @param {string} name
 * @returns {string}
 */
const getInitials = (name) => {
  if (!name) return "";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

/**
 * Circular avatar with a colored ring. Purple ring + sparkle icon is reserved
 * for the AI bot. Optionally shows an image, or a presence dot badge.
 *
 * @param {Object} props
 * @param {string} [props.name] - Display name; initials are derived from it.
 * @param {number} [props.colorIndex=0] - Stable coach color index (0-7).
 * @param {boolean} [props.isAI=false] - Render as the AI avatar (purple + sparkles).
 * @param {'xs'|'sm'|'md'|'lg'} [props.size='md'] - Avatar size.
 * @param {'online'|'offline'} [props.presence] - Optional presence dot.
 * @param {string} [props.imageUrl] - Optional avatar image URL.
 */
export const AvatarRing = ({
  name,
  colorIndex = 0,
  isAI = false,
  size = "md",
  presence,
  imageUrl,
}) => {
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const colorClass = isAI
    ? "ring-ai bg-ai-bg text-ai"
    : COACH_RING_CLASSES[colorIndex] || COACH_RING_CLASSES[0];

  const initials = getInitials(name);
  const label = isAI ? "Flex AI" : name || "User";

  return (
    <span className="relative inline-flex flex-shrink-0">
      <span
        role="img"
        aria-label={label}
        className={`inline-flex items-center justify-center rounded-full ring-2 font-semibold overflow-hidden ${sizeClass} ${colorClass}`}
      >
        {isAI ? (
          <HiSparkles aria-hidden="true" />
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={label}
            className="w-full h-full rounded-full object-cover"
          />
        ) : initials ? (
          <span aria-hidden="true">{initials}</span>
        ) : (
          <FaUserCircle aria-hidden="true" className="w-full h-full" />
        )}
      </span>
      {presence && (
        <span
          aria-label={presence === "online" ? "Online" : "Offline"}
          className={`absolute bottom-0 right-0 block w-2.5 h-2.5 rounded-full ring-2 ring-white ${
            presence === "online" ? "bg-success" : "bg-gray-400"
          }`}
        />
      )}
    </span>
  );
};
